import type { UsFillProgress, UsOrderRow, UsInventoryFill } from './types';

export type { UsInventoryFill } from './types';
const decimal = (v: unknown): v is string => typeof v === 'string' && /^(?:0|[1-9]\d{0,23})(?:\.\d{1,12})?$/.test(v);
const states = new Set(['clearing', 'established', 'reversed', 'unconfirmed']);
/** Optional enrichment must never hide readable holdings or manufacture evidence. */
export function parseUsInventoryFills(value: unknown): readonly UsInventoryFill[] {
  if (!Array.isArray(value) || value.length > 10_000) return [];
  const rows: UsInventoryFill[] = [];
  const seen = new Set<string>();
  for (const r of value) {
    if (!r || typeof r !== 'object' || typeof r.intentId !== 'string' || !r.intentId
      || typeof r.marketSlug !== 'string' || !r.marketSlug || !['yes', 'no'].includes(r.outcome)
      || !decimal(r.filledQuantity) || /^0(?:\.0+)?$/.test(r.filledQuantity)
      || !Number.isSafeInteger(r.observedAtMs) || r.observedAtMs < 0 || !states.has(r.state)
      || seen.has(r.intentId)) return [];
    seen.add(r.intentId); rows.push({ intentId: r.intentId, marketSlug: r.marketSlug,
      outcome: r.outcome, filledQuantity: r.filledQuantity, observedAtMs: r.observedAtMs, state: r.state });
  }
  return rows;
}
/** The inventory caller has authenticated account + actor. Match exact order,
 * outcome, quantity and history version: independent order/holding reads can race.
 * Missing or older evidence keeps the fill visible, never sellable. */
export function usFillProgress(order: Pick<UsOrderRow, 'intentId' | 'target' | 'lifecycle'>,
  fills: readonly UsInventoryFill[], holdingKnown: boolean): UsFillProgress | undefined {
  const life = order.lifecycle;
  if (!life || !decimal(life.filledQuantity) || /^0(?:\.0+)?$/.test(life.filledQuantity)) return undefined;
  const fill = fills.find(f => f.intentId === order.intentId && f.marketSlug === order.target.marketSlug
    && f.outcome === order.target.outcome && f.filledQuantity === life.filledQuantity
    && f.observedAtMs === life.observedAtMs);
  if (!fill) return { state: 'unconfirmed' };
  return { state: fill.state === 'established' && !holdingKnown ? 'unconfirmed' : fill.state };
}
export function usOrderNeedsFillVisibility(row: Pick<UsOrderRow, 'fillProgress'>): boolean {
  return row.fillProgress?.state === 'clearing' || row.fillProgress?.state === 'unconfirmed';
}
